/**
 * The prompt library (SellerMate W6.3): about forty ready-made questions,
 * grouped Analyse / Optimise / Automate / Keywords / Cut waste / Visualise /
 * Create / Research, searchable. "Use" hands the prompt text to `onUse`
 * (the side panel drops it into its composer for editing before sending);
 * "Schedule" hands it to `onSchedule` when the caller supports that.
 */
import { useMemo, useState } from 'react'
import { CalendarClock, Search } from 'lucide-react'
import { Button, Input, Skeleton } from '../ui'
import { ErrorState } from '../ErrorState'
import { useApiQuery } from '../../lib/query/useApiQuery'
import { aiApi, aiKeys, type LibraryPrompt } from '../../lib/aiApi'
import { ALL_CATEGORIES, filterPrompts, groupPrompts } from '../../lib/promptLibrary'
import { cn } from '../../lib/cn'

export default function PromptLibrary({
  onUse, onSchedule,
}: {
  onUse: (prompt: LibraryPrompt) => void
  onSchedule?: (prompt: LibraryPrompt) => void
}) {
  const lib = useApiQuery(aiKeys.library(), aiApi.library, { staleTime: 60 * 60_000 })
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState<string>(ALL_CATEGORIES)

  const categories = useMemo(() => lib.data?.categories ?? [], [lib.data])
  const groups = useMemo(
    () => groupPrompts(filterPrompts(lib.data?.prompts ?? [], categories, query, category), categories),
    [lib.data, categories, query, category],
  )

  if (lib.error) return <ErrorState error={lib.error} onRetry={() => void lib.refetch()} />

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" aria-hidden />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search prompts…"
          aria-label="Search prompts"
          className="w-full pl-8"
        />
      </div>
      <div className="flex flex-wrap gap-1" role="group" aria-label="Prompt categories">
        {[{ key: ALL_CATEGORIES, label: 'All' }, ...categories].map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => setCategory(c.key)}
            aria-pressed={category === c.key}
            className={cn(
              'px-2 py-0.5 rounded-full text-xs font-medium border transition-colors',
              category === c.key
                ? 'bg-accent-soft text-accent-soft-fg border-accent-edge'
                : 'bg-surface text-ink-muted border-edge hover:text-ink hover:bg-surface-2',
            )}
          >
            {c.label}
          </button>
        ))}
      </div>

      {lib.isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-14" />
          <Skeleton className="h-14" />
          <Skeleton className="h-14" />
        </div>
      )}

      {!lib.isLoading && groups.length === 0 && (
        <p className="text-sm text-ink-muted py-6 text-center">No prompts match that search.</p>
      )}

      {groups.map((g) => (
        <section key={g.category.key} aria-label={g.category.label}>
          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-muted mb-1">
            {g.category.label}
            <span className="ml-2 normal-case tracking-normal font-normal text-ink-faint">{g.category.blurb}</span>
          </div>
          <ul className="space-y-1.5">
            {g.prompts.map((p) => (
              <li key={p.id} className="rounded-lg border border-edge bg-surface p-2.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-ink">{p.title}</div>
                    <p className="text-xs text-ink-muted mt-0.5 line-clamp-2">{p.prompt}</p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    {onSchedule && (
                      <Button
                        size="xs" variant="ghost"
                        onClick={() => onSchedule(p)}
                        aria-label={`Schedule “${p.title}”`}
                        title="Run this on a schedule"
                      >
                        <CalendarClock size={12} aria-hidden />
                      </Button>
                    )}
                    <Button size="xs" variant="soft" onClick={() => onUse(p)}>Use</Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}
